import { Box, Stack } from '@mui/material'
import { GroupedValuationDeviationOptions, ValuationDeviationOption, ValuationDeviationOptions } from './types'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { ContentChip, isoDateStringToLocalFormat, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { mapValues } from 'lodash'

export function mapValuationOptionsToSelectOptions({
    valuationOptions,
    grouped,
    lockedBaseValuationId,
    lockBaseValuationTitle,
    locale = 'de-DE',
    copy = DEFAULT_VALUATION_OPTION_SELECT_COPY,
}: {
    valuationOptions?: ValuationDeviationOptions
    grouped?: boolean
    lockedBaseValuationId?: string
    lockBaseValuationTitle?: string
    locale?: string
    copy?: ValuationOptionSelectCopy
}) {
    return valuationOptions?.map((valuationOption) =>
        mapValuationOptionToSelectOption({
            valuationOption,
            grouped,
            lockedBaseValuationId,
            lockBaseValuationTitle,
            locale,
            copy,
        })
    )
}

export type ValuationOptionSelectCopy = {
    lockedDraftTitle: string
    lockedValuationTitle: string
    draftStatus: string
    finalStatus: string
}

const DEFAULT_VALUATION_OPTION_SELECT_COPY: ValuationOptionSelectCopy = {
    lockedDraftTitle: 'Dieser Entwurf',
    lockedValuationTitle: 'Dieses Gutachten',
    draftStatus: 'Entwurf',
    finalStatus: 'Final',
}

export function mapGroupedValuationOptionsToSelectOptions({
    groupedValuationOptions,
    lockedBaseValuationId,
    lockBaseValuationTitle,
    locale = 'de-DE',
    copy = DEFAULT_VALUATION_OPTION_SELECT_COPY,
}: {
    groupedValuationOptions?: GroupedValuationDeviationOptions
    lockedBaseValuationId?: string
    lockBaseValuationTitle?: string
    locale?: string
    copy?: ValuationOptionSelectCopy
}) {
    return mapValues(groupedValuationOptions, (group) => ({
        ...group,
        options:
            mapValuationOptionsToSelectOptions({
                valuationOptions: group.options,

                grouped: true,
                lockedBaseValuationId,
                lockBaseValuationTitle,
                locale,
                copy,
            }) ?? [],
    }))
}

function mapValuationOptionToSelectOption({
    valuationOption,
    grouped,
    lockedBaseValuationId,
    lockBaseValuationTitle,
    locale,
    copy,
}: {
    valuationOption: ValuationDeviationOption
    grouped?: boolean
    lockedBaseValuationId?: string
    lockBaseValuationTitle?: string
    locale: string
    copy: ValuationOptionSelectCopy
}) {
    const { value, title, valuationVersion, valuationDate, date } = valuationOption
    const optionLabel = grouped ? valuationVersion : title
    const lockedTitle = lockBaseValuationTitle ?? (valuationOption.isDraft ? copy.lockedDraftTitle : copy.lockedValuationTitle)
    const formattedDate = isoDateStringToLocalFormat(date ?? valuationDate, false, locale)
    const statusChip = valuationOption.isDraft
        ? { label: copy.draftStatus, color: 'orange' as const }
        : valuationOption.status === 'finalised'
          ? { label: copy.finalStatus, color: 'green' as const }
          : undefined
    return {
        value,
        label: (
            <Stack width="100%" alignItems="flex-end">
                <TextWithEllipsis
                    tooltipText={optionLabel ?? ''}
                    typographyProps={{ variant: 'textM', color: tokens.ColorTextBold }}
                    textAlign="right"
                    fullWidth
                >
                    {lockedBaseValuationId === value ? lockedTitle : optionLabel}
                </TextWithEllipsis>
                {(formattedDate || statusChip) && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        flexWrap="wrap"
                        columnGap={tokens.SizingSpacing050}
                        width="100%"
                    >
                        {formattedDate && (
                            <Typography variant="textXS" color={tokens.ColorTextSubtle} noWrap>
                                {formattedDate}
                            </Typography>
                        )}
                        {statusChip && (
                            <Box minWidth="fit-content">
                                <ContentChip size="small" highContrast color={statusChip.color} label={statusChip.label} />
                            </Box>
                        )}
                    </Stack>
                )}
            </Stack>
        ),
    }
}
